import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
  EnrollmentCallGapDay,
  EnrollmentCallGapSegment,
  EnrollmentCallGapsParams,
  EnrollmentCallGapsResponse,
} from "@t3tools/contracts";
import { Button } from "~/components/ui/button";
import { KpiCard } from "./dashboardKit";
import {
  callGapClock,
  callGapDuration,
  callGapTotals,
  enrollmentCallGapsQueryOptions,
  lastFiveWorkingDays,
  previousDate,
} from "./enrollmentCallGaps";
import { todayIso } from "./enrollmentShared";

const callColor = "#176C92",
  longGapColor = "#F1AE51",
  shortGapColor = "#E8EDF1";
const inputClass =
  "rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground";
const gapNames = {
  before: "Before first call",
  between: "Between calls",
  after: "After last call",
  no_calls: "No qualifying call activity recorded",
};

function segmentDescription(segment: EnrollmentCallGapSegment) {
  const label = segment.kind === "call" ? "Call activity" : gapNames[segment.gapType ?? "between"];
  return `${label}: ${callGapClock(segment.startMinute)}–${callGapClock(segment.endMinute)} (${(segment.endMinute - segment.startMinute).toFixed(2)} minutes)`;
}

function Timeline({ day }: { day: EnrollmentCallGapDay }) {
  if (day.shiftStartMinute === null)
    return <span className="text-xs text-muted-foreground">No scheduled shift</span>;
  return (
    <svg
      role="img"
      aria-label={`${day.specialistName}, ${day.date}: call timeline`}
      viewBox="0 0 840 30"
      className="h-8 w-full min-w-120"
    >
      {Array.from({ length: 15 }, (_, index) => (
        <line
          key={index}
          x1={index * 60}
          x2={index * 60}
          y1={0}
          y2={30}
          stroke="currentColor"
          opacity={0.12}
        />
      ))}
      {day.segments.map((segment) => (
        <g key={`${segment.kind}:${segment.startMinute}`}>
          <rect
            x={segment.startMinute - 420}
            y={5}
            width={segment.endMinute - segment.startMinute}
            height={20}
            fill={
              segment.kind === "call"
                ? callColor
                : segment.endMinute - segment.startMinute >= 15 - 1e-9
                  ? longGapColor
                  : shortGapColor
            }
            tabIndex={0}
            aria-label={segmentDescription(segment)}
          >
            <title>{segmentDescription(segment)}</title>
          </rect>
          {segment.kind === "gap" && segment.endMinute - segment.startMinute >= 35 ? (
            <text
              x={(segment.startMinute + segment.endMinute) / 2 - 420}
              y={19}
              textAnchor="middle"
              fontSize={10}
              fill="#67400C"
              pointerEvents="none"
            >
              {Math.round(segment.endMinute - segment.startMinute)}m
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

export function EnrollmentCallGapChart({
  data,
  individual = false,
}: {
  data: EnrollmentCallGapsResponse;
  individual?: boolean;
}) {
  const totals = callGapTotals(data.days);
  const noActivity = data.days.filter((day) => day.shiftStartMinute !== null && day.calls === 0);
  const gapRows = data.days.flatMap((day) =>
    day.segments.filter((segment) => segment.kind === "gap").map((segment) => ({ day, segment })),
  );
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-950">
        Does not account for lunch break or time clocked out
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Calls in shift" value={totals.calls.toLocaleString()} />
        <KpiCard label="Total non-call time" value={callGapDuration(totals.nonCallMinutes)} />
        <KpiCard
          label="Non-call time in gaps ≥15 min"
          value={callGapDuration(totals.longGapMinutes)}
        />
        <KpiCard label="Gaps ≥15 min" value={totals.longGapCount.toLocaleString()} />
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table
          className="w-full min-w-275 text-left text-xs"
          aria-label={
            individual
              ? "Specialist daily call activity and gaps"
              : "Full-team call activity and gaps"
          }
        >
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="px-3 py-3">{individual ? "Date" : "Enrollment specialist"}</th>
              <th className="w-[48%] px-3 py-3">
                <div className="flex justify-between text-[10px] font-normal">
                  {["7a", "9a", "11a", "1p", "3p", "5p", "7p", "9p"].map((hour) => (
                    <span key={hour}>{hour}</span>
                  ))}
                </div>
                <span className="sr-only">Call timeline, Eastern Time</span>
              </th>
              <th className="px-3 py-3">Calls</th>
              <th className="px-3 py-3">Non-call</th>
              <th className="px-3 py-3">In gaps ≥15m</th>
              <th className="px-3 py-3">Gaps ≥15m</th>
            </tr>
          </thead>
          <tbody>
            {data.days.map((day) => (
              <tr
                key={`${day.specialistId}:${day.date}`}
                className="border-b border-border/60 last:border-0 even:bg-muted/25"
              >
                <th scope="row" className="whitespace-nowrap px-3 py-2 font-medium">
                  {individual ? day.date : day.specialistName}
                  <span className="block text-[10px] font-normal text-muted-foreground">
                    {day.shiftStartMinute === null || day.shiftEndMinute === null
                      ? "Not scheduled"
                      : `${callGapClock(day.shiftStartMinute).replace(":00 ", " ")}–${callGapClock(day.shiftEndMinute).replace(":00 ", " ")}`}
                  </span>
                </th>
                <td className="px-3 py-2">
                  <Timeline day={day} />
                </td>
                <td className="px-3 py-2 tabular-nums">{day.calls}</td>
                <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums">
                  {day.shiftStartMinute === null ? "—" : callGapDuration(day.nonCallMinutes)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums">
                  {day.shiftStartMinute === null ? "—" : callGapDuration(day.longGapMinutes)}
                </td>
                <td className="px-3 py-2 tabular-nums">{day.longGapCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.days.length ? (
        <p role="status" className="text-sm text-muted-foreground">
          No dates match the selected filters.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-5 text-xs">
        {[
          [callColor, "Call activity"],
          [shortGapColor, "Gap <15 min"],
          [longGapColor, "Gap ≥15 min"],
        ].map(([color, label]) => (
          <span key={label} className="flex items-center gap-2">
            <span className="h-2.5 w-5 rounded-xs" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>
      {noActivity.length ? (
        <p role="status" className="text-xs text-muted-foreground">
          No qualifying call activity recorded:{" "}
          {noActivity.map((day) => (individual ? day.date : day.specialistName)).join(", ")}. Full
          scheduled shifts are included in non-call totals; this does not establish absence or idle
          time.
        </p>
      ) : null}
      <p className="text-xs leading-relaxed text-muted-foreground">
        Eastern Time. Includes outbound attempts and answered incoming calls, with dialing/ringing
        time. Calls are clipped to the supplied recurring shifts and overlaps count once. Non-call
        totals include gaps before the first call and after the last call. The entire duration of
        each gap ≥15 minutes counts. Rounded to the nearest minute. Other work and breaks can occur
        during gaps.
      </p>
      <p className="text-xs text-muted-foreground">
        Schedule assumptions: Oquay Clark 9:00 AM–5:30 PM; Laura Ciufia Monday–Thursday; Crystal
        Roberson Monday/Tuesday/Thursday 5–9 PM, Saturday 8 AM–2 PM, Sunday 2–6 PM. Other
        specialists Monday–Friday. Supplied recurring schedules are applied to the selected dates.
      </p>
      <details className="rounded-md border border-border px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">
          View all gaps ({gapRows.length})
        </summary>
        <div className="mt-3 max-h-96 overflow-auto">
          <table className="w-full text-left text-xs" aria-label="Individual call gaps">
            <thead>
              <tr>
                {["Specialist", "Date", "Start ET", "End ET", "Minutes", "Gap type"].map(
                  (heading) => (
                    <th key={heading} className="px-2 py-2">
                      {heading}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {gapRows.map(({ day, segment }) => (
                <tr
                  key={`${day.specialistId}:${day.date}:${segment.startMinute}`}
                  className={
                    segment.endMinute - segment.startMinute >= 15 - 1e-9
                      ? "bg-amber-100 text-amber-950"
                      : ""
                  }
                >
                  <td className="px-2 py-1.5">{day.specialistName}</td>
                  <td className="px-2 py-1.5">{day.date}</td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {callGapClock(segment.startMinute)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {callGapClock(segment.endMinute)}
                  </td>
                  <td className="px-2 py-1.5 tabular-nums">
                    {(segment.endMinute - segment.startMinute).toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5">{gapNames[segment.gapType ?? "between"]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      <p className="text-[10px] text-muted-foreground">
        Saved Dialpad records · Updated{" "}
        {new Date(data.generatedAt).toLocaleString("en-US", { timeZone: "America/New_York" })} ET
      </p>
    </div>
  );
}

function useCallGapReport(params: EnrollmentCallGapsParams) {
  const invalid =
    !params.start ||
    !params.end ||
    params.start > params.end ||
    Date.parse(params.end) - Date.parse(params.start) > 30 * 86_400_000;
  const query = useQuery({ ...enrollmentCallGapsQueryOptions(params), enabled: !invalid });
  return { invalid, query };
}

function QueryChart({
  report: { invalid, query },
  individual = false,
}: {
  report: ReturnType<typeof useCallGapReport>;
  individual?: boolean;
}) {
  if (invalid)
    return (
      <p role="alert" className="text-sm text-destructive">
        Select a valid date range of at most 31 days.
      </p>
    );
  if (query.error)
    return (
      <div role="alert" className="text-sm text-destructive">
        {query.error.message}{" "}
        <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  if (!query.data)
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading Dialpad call activity…
      </p>
    );
  return <EnrollmentCallGapChart data={query.data} individual={individual} />;
}

function DateField({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: string;
  max: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium">
      {label}
      <input
        type="date"
        className={inputClass}
        value={value}
        max={max}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function EnrollmentCallGapsSection() {
  const defaults = useMemo(
    () => ({ today: todayIso(), range: lastFiveWorkingDays(todayIso()) }),
    [],
  );
  const max = previousDate(defaults.today);
  const [date, setDate] = useState(max);
  const [start, setStart] = useState(defaults.range.start);
  const [end, setEnd] = useState(defaults.range.end);
  const [specialist, setSpecialist] = useState("bethany-keller");
  const [weekdaysOnly, setWeekdaysOnly] = useState(true);
  const team = useCallGapReport({ start: date, end: date });
  const history = useCallGapReport({ start, end, specialist, weekdaysOnly });
  const availableSpecialists = history.query.data?.specialists ?? team.query.data?.specialists;
  // Keep the last successful roster available while either report changes filters or fails.
  const [specialists, setSpecialists] = useState(availableSpecialists);
  useEffect(() => {
    if (availableSpecialists) setSpecialists(availableSpecialists);
  }, [availableSpecialists]);
  const frame = (title: string, controls: ReactNode, chart: ReactNode) => (
    <section
      aria-label={title}
      className="flex flex-col gap-4 rounded-lg border border-border bg-background p-4"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h3 className="text-base font-semibold">{title}</h3>
        <div className="flex flex-wrap items-end gap-3">{controls}</div>
      </div>
      {chart}
    </section>
  );
  return (
    <div className="mt-4 flex flex-col gap-6">
      {frame(
        "Full-team Dialpad call gaps",
        <DateField
          label="Team analysis date"
          value={date}
          max={defaults.today}
          onChange={setDate}
        />,
        <QueryChart report={team} />,
      )}
      {frame(
        "Enrollment specialist call gaps",
        <>
          <label className="flex flex-col gap-1 text-xs font-medium">
            Enrollment specialist
            <select
              className={inputClass}
              value={specialist}
              onChange={(event) => setSpecialist(event.target.value)}
            >
              {specialists?.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              )) ?? <option value="bethany-keller">Bethany Keller</option>}
            </select>
          </label>
          <DateField label="Specialist start date" value={start} max={max} onChange={setStart} />
          <DateField label="Specialist end date" value={end} max={max} onChange={setEnd} />
          <label className="flex items-center gap-2 py-2 text-xs">
            <input
              type="checkbox"
              checked={weekdaysOnly}
              onChange={(event) => setWeekdaysOnly(event.target.checked)}
            />
            Weekdays only
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStart(defaults.range.start);
              setEnd(defaults.range.end);
              setWeekdaysOnly(true);
            }}
          >
            Last 5 working days
          </Button>
        </>,
        <>
          <p className="text-xs text-muted-foreground">
            Defaults to the last five completed Monday–Friday days, excluding today. Turn off
            “Weekdays only” to include weekend shifts.
          </p>
          <QueryChart report={history} individual />
        </>,
      )}
    </div>
  );
}
